import httpClient from "@plugins/http";
import cryptoUtils from "@utils/crypto.utils";
import { isEmpty } from "@utils/var.check";
import { ApiResponse } from "./api";
import {
  ChangePasswordModel,
  User,
  UpdateDisplayNameRequest,
  UpdateDisplayNameResponse,
} from "@modules/profile/profile-types.ts";

interface AuthenticationSuccessResponse {
  redirectUrl: string;
}

export default {
  fetchProfileInfo(): Promise<ApiResponse<User>> {
    return httpClient.get(`/apis/profile/info`);
  },
  updateName: (
    requestData: UpdateDisplayNameRequest,
  ): Promise<ApiResponse<UpdateDisplayNameResponse>> => {
    return httpClient.post("/apis/profile/displayname", requestData);
  },
  updateSecurityQuestions: (requestData): Promise<ApiResponse<Record<string, unknown>>> => {
    return httpClient.post("/apis/profile/securityquestions/update", requestData);
  },
  fetchSecurityQuestions: (): Promise<ApiResponse<Record<string, unknown>>> => {
    return httpClient.get("/apis/profile/securityquestions/get");
  },
  updatePassword: (
    requestData: ChangePasswordModel,
  ): Promise<ApiResponse<Record<string, string>>> => {
    return httpClient.post("/apis/profile/password/update", requestData);
  },
  removeEmail: (): Promise<ApiResponse<boolean>> => {
    return httpClient.post("/apis/profile/email/remove");
  },
  changeEmail: (useOriginalEmail: boolean): Promise<ApiResponse<boolean>> => {
    return httpClient.post(`/apis/profile/email/change/${useOriginalEmail}`);
  },
  sendOTP: (email) => {
    return httpClient.post(`/apis/profile/otp/request`, email);
  },
  resendOTP: (email) => {
    return httpClient.post(`/apis/profile/otp/resend`, email);
  },
  verifyOTP: (requestData) => {
    return httpClient.post(`/apis/profile/email/verify`, null, { params: requestData });
  },
  verifyAdminOTP: (requestData) => {
    return httpClient.post(`/apis/admin/profile/email/verify`, null, { params: requestData });
  },
  verifyPassword: (password) => {
    return cryptoUtils.encrypt(password).then((encrypted) => {
      return httpClient.post(`/apis/profile/password/validate`, {
        password: encrypted,
      });
    });
  },
  switchAccount: (requestData): Promise<ApiResponse<AuthenticationSuccessResponse>> => {
    const password = requestData?.password;
    const loginFlow = requestData?.loginFlow;

    if (!isEmpty(password)) {
      return cryptoUtils.encrypt(password).then((encrypted) => {
        return httpClient.post(`/apis/profile/account/switch`, {
          password: encrypted,
          loginFlow: loginFlow,
        });
      });
    } else {
      // For teacher switch to associate student account, no need to provide password
      return httpClient.post(`/apis/profile/account/switch`, {
        loginFlow: loginFlow,
      });
    }
  },
  checkAndRemindUserForEmptyEmail: () => {
    return httpClient.post(`/apis/profile/check-user-reset-email`);
  },

  fetchUserStats: (userId) => {
    return httpClient.get(`/apis/profile/stats`, {
      params: { userId: userId },
    });
  },
  fetchUserProfileInfo: (userId) => {
    return httpClient.get(`/apis/profile/user-info`, {
      params: { userId: userId },
    });
  },
  updateUserOnlineStatus: () => {
    //SLS-11210: Live indicator - to set heartbeat
    return httpClient.post(`/apis/user/online-status/keep-alive`);
  },
  getOnlineStatusForUsers: (userIds) => {
    return httpClient.get(`/apis/user/online-status`, {
      params: { userIds: userIds },
    });
  },
};
